import React, { useContext, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import Checkbox from './Checkbox';
import QuantityInput from './QuantityInput';
import ItemPrice from './ItemPrice';
import CancelItemSubscription from '../Forms/CancelItemSubscrition';
import Modal from '../../common/Modal/Modal';
import SelectItemFrequency from '../Forms/SelectItemFrequency';
import appLabels from '../../utils/appLabels';
import { otaModalConfigId, SNS_LINK } from '../../utils/constants';
import SkipItemOnceLink from './SkipItemOnceLink';
import { get, invoke, find, filter, map, size, replace, cond } from 'lodash';
import { useApp } from '../../context/app-context';
import formatString from '../../utils/formatString';
import updateItemInfo from '../../service/update-item-info-service';
import cancelSubscription from '../../service/cancel-subscription-service';
import Loader from '../../common/Loading/loader';
import Asset from '../Order/Asset';
import { ItemsContext, ItemsContextType } from './ItemsContext';
import OrderInfoIcon from '../Order/OrderInfoIcon';
import moment from 'moment';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';
import EditSubstitutionLink from './EditSubstitutionLink';
import { ENABLE_OTA_OFFERS, isMobile, isMobileScreen } from '../../utils/utils';
import saveOtaToSnsItemApi from '../../service/save-ota-to-sns-item';

const mainContainerStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem 0;
  border-bottom: 0.063rem solid #f1f0ee;
`;

const itemContainerStyle = css`
  position: relative;
  display: flex;
`;

const descriptionContainerStyle = css`
  display: flex;
  align-items: flex-start;
`;

const itemInfoStyle = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0 1rem;

  @media (max-width: 641px) {
    padding: 0;
  }
`;

const dateStyle = css`
  margin-bottom: 1rem;
  color: #000000;
`;

const itemNameStyle = css`
  font-weight: 600 !important;
  margin-bottom: 1rem;
  line-height: 1.25rem !important;
  flex: 1 0 0;

  @media (max-width: 641px) {
    flex: 1 0 40%;
  }
`;

const ItemImageContainer = css`
  background: #ccc;
  width: 6rem;
  height: 6rem;
  min-width: 6rem;

  @media (max-width: 641px) {
    width: 4rem;
    height: 4rem;
    min-width: 4rem;
  }
`;

const assetStyle = css`
  @media (max-width: 641px) {
    width: 4rem;
    height: 4rem;
  }
`;

const itemContentAssetStyle = css`
  width: 16.125rem;
  height: 14.688rem;
  @media (max-width: 641px) {
    width: 8rem;
    height: 8rem;
  }
`;

const checkboxRowStyle = css`
  width: fit-content;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.2rem;

  & > svg {
    cursor: pointer;
  }
`;

const warningIconStyle = css`
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  @media (max-width: 641px) {
    width: 24px;
    height: 24px;
  }
`;

const baseFrequencyStyle = css`
  display: flex;
  align-items: center;
  font-family: 'Nunito Sans', Tahoma, sans-serif;
  margin-bottom: 0.5rem;
  background: white;
  border: none;
  width: fit-content;

  & > svg {
    margin-right: 0.5rem;
  }
`;

const selectFrequencyStyle = css`
  cursor: pointer;
  color: #7b1a27;
`;

const cancelledFrequencyStyle = css`
  cursor: default;
  color: #2a2928;
`;

const buttonsContainerStyle = css`
  display: flex;
  color: #7b1a27;
  padding-top: 1rem;

  & > div {
    border-right: 0.063rem solid #ddd9d5;
  }

  & > div:first-child > div > button {
    padding-left: 0;
  }

  & > div:last-child {
    border-right: none;
  }
  @media (max-width: 380px) {
    button {
      font-size: 0.7rem;
    }
  }
`;

const couponsAndSavingsButtonStyle = css`
  border: 0;
  background: 0;
  cursor: pointer;
  color: #04633c;
  text-align: left;
  padding-left: 0;
`;

const noUnderline = css`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5em;
  text-decoration: none !important;
  color: inherit;
  cursor: auto !important;
`;

const couponsAndSavingsSpanStyle = css`
  font-size: 0.875rem;

  @media (max-width: 380px) {
    white-space: nowrap;
    font-size: 0.8rem;
  }
`;
const subscriptionWithoutSavingStyle = css`
  font-size: 0.875rem;
  font-weight: 400;
  color: #1f1e1e;

  @media (max-width: 380px) {
    white-space: nowrap;
    font-size: 0.8rem;
  }
`;

const infoIconStyle = css`
  display: flex;
  height: 1rem;
  margin-left: 0.5rem;
`;

const cancelSubscriptionButtonStyle = css`
  border: 0;
  background: 0;
  cursor: pointer;
  color: #7b1a27;
`;

const cancelledQuantityStyle = css`
  width: 2.5rem;
  height: 2.5rem;
  border: 0.0625rem solid #ddd9d5;
  border-radius: 6.25rem;
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const allowSubstitutionTooltipStyle = css`
  background: 0;
  border: 0;
  padding: 0;
  display: flex;
`;

const frequencyLinkStyle = css`
  text-decoration: underline;
`;

const actionsRowStyle = css`
  display: flex;
  flex-direction: row;

  & div {
    border-right: 0.063rem solid #ddd9d5;
  }
  & div:last-child {
    border-right: initial;
  }
`;

const frequencyButtonStyle = css`
  color: #7b1a27;
  cursor: pointer;
  border: 0;
  background: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none !important;
  white-space: nowrap;
  padding-left: 0;

  span.frequencyLabelText {
    text-decoration: underline;
    margin-left: -5px;
  }
  @media (max-width: 380px) {
    font-size: 0.8rem;
  }
`;

const repeatTextStyle = css`
  @media (max-width: 641px) {
    display: none;
  }
`;

const upcomingOrderSubstitutionLinkStyle = css`
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  color: #7b1a27;
`;
const offerFontStyle = css`
  color: #04633c;
  font-weight: 600;
  margin-bottom: 0.625rem;
  display: flex;
  align-items: baseline;

  button.link-sm {
    padding-left: 0;
  }
`;

const noFlexibleDiscountInfoIconCenter = css`
  align-items: center;
`;

const editSubstitutionLink = css`
  font-weight: 600;
  width: initial;
  margin-top: 1rem;
  padding: 0.5rem 0;
  border: 0.063rem solid #e5e4e3;
  border-radius: 0.25rem;
`;
const accordionIconStyle = css`
  display: flex;
  margin: 0.063rem 0.5rem 0 0;
`;

const flexRow = css`
  display: flex;
  align-items: center;
`;

const contentWrapperStyle = css`
  @media (max-width: 380px) {
  }
  @media (max-width: 641px) {
  }
  @media (max-width: 768px) {
  }
  @media (min-width: 1024px) {
    width: 45rem;
    max-width: 45rem;
  }
`;

const otaToSnsContainerStyle = css`
  padding-top: 1rem;
`;

const otaToSnsCheckBoxTextStyle = css`
  font-weight: 400;
  font-size: 0.875rem;
  color: #1f1e1e;
  margin-left: 0.313rem;
`;

const subToOtaModal = css`
  display: flex;
  flex-direction: row;
  margin: 1.5rem;
  width: 38.5rem;
  height: 26.25rem;
  @media (max-width: 641px) {
    width: auto;
    height: auto;
    margin: 0 0.625rem 0.938rem;
  }
`;

const frequencyContainer = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 20rem;
  @media (max-width: 641px) {
    width: 12.438rem;
    height: inherit;
  }
`;

const itemContentContainer = css`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 18.5rem;
  height: 26.25rem;
  @media (max-width: 641px) {
    width: 8rem;
    height: inherit;
    margin: 0 0.625rem;
  }
`;

const subToOtaButtonContainer = css`
  display: flex;
  justify-content: center;
  @media (max-width: 641px) {
    flex-direction: column-reverse;
    margin-left: auto;
    margin-right: auto;
    margin-top: 1.25rem;
  }
`;

const subToOtaButton = css`
  width: 19.625rem;
  height: 3rem;
  margin: 0 0.375rem;
  min-width: 6rem;
  min-height: 3rem;
  border-radius: 6.25rem;
  background: #7b1a27;
  border: 0.063rem solid #7b1a27;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.45rem;
  text-align: center;
  cursor: pointer;
  @media (max-width: 641px) {
    width: 20.938rem;
  }
`;

const subToOtaNotNowButton = css`
  background: #ffffff;
  color: #7b1a27;
  border: 0.063rem solid #7b1a27;
  @media (max-width: 641px) {
    border: none;
    text-decoration: underline;
    font-size: 0.875rem;
  }
`;

const itemContent = css``;

const otaToSnsModalQunatityStyle = css`
  width: auto !important;
  margin: 0.75rem 0;
  justify-content: space-between !important;

  & > button {
    margin: 0 0.625rem;
  }
  @media (max-width: 641px) {
    margin: 0;
    padding: 0;
  }
`;
const frequencyWidgetStyle = css`
  display: flex;
  flex-direction: column;
  max-width: 37.5rem;
  width: initial;
  border-radius: 0.625rem;
  border: 0.063rem solid #e5e4e3;
  justify-content: flex-end;
  @media (max-width: 641px) {
  }
`;

const frequencyWidgetHeading = css`
  margin: 0.75rem 0 0.313rem 1.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.088rem;
  text-align: left;
`;

const frequencyListContainer = css`
  display: flex;
  align-items: center;
  position: relative;
  margin: 0 0 0.75rem 1rem;

  button {
    background: #fff;
    border: none;
    width: 2rem;
    height: 2rem;
    border-radius: 100%;
    box-shadow: 0 0.125rem 0.375rem #0003;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;

    &:not(:disabled) {
      cursor: pointer;
    }

    svg {
      object-fit: contain;
      width: 20px;
      height: 20px;
    }
  }
`;

const frequencyWidgetInfoContainer = css`
  color: #4b4b49;
  padding: 0.5rem 1rem;
  background: #faf9f8;
  border-bottom-left-radius: 0.625rem;
  border-bottom-right-radius: 0.625rem;
`;
const frequencyWidgetInfo = css`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.088rem;
  text-align: left;
  span {
    font-weight: 700;
  }
`;

const frequencyItemsContainer = css`
  display: flex;
  -moz-column-gap: 0.625rem;
  gap: 0.625rem;
  top: 0.75rem;
  column-gap: 0.625rem;
  position: relative;
  flex-wrap: nowrap;
  transform: translateX(0px);
  transition: transform 0.2s ease-in-out;
  scroll-behavior: smooth;
  overflow-x: auto;
  cursor: pointer;
  ::-webkit-scrollbar {
    display: none;
  }
`;
const frequencyActive = css`
  display: flex;
  position: relative;
  align-items: center;
  border: 0.092rem solid #8c0e12 !important;
  span {
    color: #8c0e12 !important;
  }
`;

const frequencyItemContainer = css`
  position: relative;
`;

const frequencyItem = css`
  display: flex;
  height: 4.188rem;
  min-width: 4.875rem;
  border-radius: 0.625rem;
  border: 0.031rem solid #e5e4e3;
  position: relative;
  align-items: center;
  text-align: center;
  justify-content: center;
  color: #535250;
  flex-direction: column;
  svg {
    height: 21px;
    width: 23px;
    vertical-align: middle;
    border-style: none;
  }
  span {
    display: flex;
    height: 1.25rem;
    line-height: 1.088rem;
    color: #1f1e1e;
    font-size: 0.75rem;
  }
`;

//TODO: Need to fix redundant property
const frequencyItemActiveCurl = css`
  content: '';
  position: absolute;
  width: 63%;
  height: 64%;
  border-top-color: transparent !important;
  border-bottom-color: transparent !important;
  border-right-color: transparent !important;
  border-width: 0.092rem;
  border-style: solid;
  top: -39%;
  left: 71%;
  border-radius: 50%;
  transform: rotate(-45deg);
  background-color: #fff;
  border-color: #8c0e12;
`;

const frequencyLeftArrowPrevBtn = css`
  left: -0.938rem;
  top: 1.875rem;
`;
const frequencyRightArrowNextBtn = css`
  right: 0rem;
  top: 1.875rem;
`;
const rightTick = css`
  width: 0.313rem;
  height: 0.5rem;
  transform: rotate(45deg);
  position: absolute;
  right: 0.188rem;
  top: 0;
  border: 0.125rem none #8c0e12;
  border-right-style: solid;
  border-bottom-style: solid;
`;

const itemPriceContainerStyle = css`
  display: flex;
  align-items: baseline;
  line-height: 0.906rem;
`;
const itemPriceDiscountedStyle = css`
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.35rem;
`;

//TODO remove redundant font-size
const itemPriceRegularStyle = css`
  font-size: 0.625rem;
  color: #4b4b49;
  margin-left: 0.25rem;
`;

const itemHighlightStyle = css`
  background: #fef173;
  border-radius: 0.25rem;
  padding-left: 0.125rem;
  padding-right: 0.125rem;
`;

const itemPriceStrikeThroughStyle = css`
  text-decoration: line-through;
`;

const approxLabelTextStyle = css`
  color: #4b4b49;
  height: 0.938rem;
  width: auto;
  font-size: 0.75rem;
  margin: 0;
  line-height: 1.088rem;
  @media (max-width: 641px) {
    margin-top: 0.313rem;
  }
`;

const itemDescriptionTextStyle = css`
  font-size: 1rem;
  font-weight: 400;
  color: #1f1e1e;
  line-height: 1.45rem;
  width: 20rem;
  margin: 0.5rem 0;
  @media (max-width: 641px) {
    width: auto;
    font-size: 0.875rem;
    line-height: 1.138rem;
    margin: 0.5rem 0 0.8rem;
  }
`;

const GreenTickIcon = () => (
  <div className={accordionIconStyle}>
    <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.9801 0.373072C13.2983 0.638245 13.3413 1.11117 13.0762 1.42938L5.57617 10.4294C5.44127 10.5913 5.24449 10.6889 5.03399 10.6985C4.82348 10.708 4.61867 10.6286 4.46967 10.4796L0.469673 6.47961C0.176778 6.18672 0.176776 5.71185 0.469667 5.41895C0.762559 5.12606 1.23743 5.12605 1.53033 5.41895L4.9496 8.83818L11.9238 0.469099C12.189 0.150892 12.6619 0.107899 12.9801 0.373072Z"
        fill="#04633C"
      />
    </svg>
  </div>
);
// method to format next fulfillment date to : Next: Wed Mar 22, 10 AM - 2 PM
const formatNextFulfillmentDate = (nextFulfillmentDate, fulfillmentStartTime, fulfillmentEndTime) => {
  const date = moment(nextFulfillmentDate, 'YYYY-MM-DD');
  return `Next: ${date.format('ddd MMM D')}, ${fulfillmentStartTime} - ${fulfillmentEndTime}`;
};

const FrequencyWidget = ({ frequencyOptions, onChange }) => {
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [selectedFrequencyWeek, setSelectedFrequencyWeek] = useState('');
  const visibleBoxes = 3;
  const boxWidth = 80;

  const frequencyBoxContainerRef = useRef<HTMLDivElement>(null);

  // Function to handle frequency change
  const handleFrequencyChange = (frequency) => {
    const formattedFrequency = formatFrequency(frequency);
    setSelectedFrequency(get(frequency, 'displayFrequency', ''));
    setSelectedFrequencyWeek(formattedFrequency);
    onChange(frequency);
  };

  // Function to format the displayFrequency correctly
  const formatFrequency = (frequency) => {
    return get(frequency, 'count', 0) === 1 ? '1 week' : get(frequency, 'displayFrequency', '');
  };

  const scrollRight = () => {
    if (frequencyBoxContainerRef.current) {
      frequencyBoxContainerRef.current.scrollLeft += visibleBoxes * boxWidth;
    }
  };

  const scrollLeft = () => {
    if (frequencyBoxContainerRef.current) {
      frequencyBoxContainerRef.current.scrollLeft -= visibleBoxes * boxWidth;
    }
  };

  // Pre-select the default frequency on component load
  useEffect(() => {
    const defaultFrequency = find(frequencyOptions, (frequency) => get(frequency, 'isDefault', false));
    if (defaultFrequency) {
      setSelectedFrequency(get(defaultFrequency, 'displayFrequency', ''));
      setSelectedFrequencyWeek(formatFrequency(defaultFrequency));
      onChange(defaultFrequency);
    }
  }, [frequencyOptions]);

  return (
    <div className={frequencyWidgetStyle} data-testid="frequency-widget">
      <div className={frequencyWidgetHeading}>
        {formatString(appLabels.PendingOrderContent.frequencyWidgetHeadingText, {
          selectedWeek: selectedFrequencyWeek,
        })}
      </div>
      <div className={frequencyListContainer}>
        <div
          className={frequencyItemsContainer}
          tabIndex={-1}
          id="freqDropdownContentList_"
          role="menu"
          ref={frequencyBoxContainerRef}
        >
          {map(frequencyOptions, (frequency, index) => (
            <div
              key={index}
              className={`${frequencyItem} ${selectedFrequency === get(frequency, 'displayFrequency') ? frequencyActive : ''}`}
            >
              {selectedFrequency === get(frequency, 'displayFrequency') && (
                <div className={frequencyItemActiveCurl}></div>
              )}
              {selectedFrequency === get(frequency, 'displayFrequency') && <div className={rightTick}></div>}
              <div
                tabIndex={0}
                role="button"
                onClick={() => handleFrequencyChange(frequency)}
                data-testid="frequnecy-button"
              >
                {selectedFrequency === get(frequency, 'displayFrequency') ? <RefreshIconRed /> : <RefreshIconBlack />}
                <span className="freq-text">
                  <span className="ng-tns-c106-0" id={`display-freq-${index}`}>
                    {formatFrequency(frequency)}
                  </span>
                </span>
              </div>
            </div>
          ))}
        </div>
        <button id="prevBtn" className={frequencyLeftArrowPrevBtn} onClick={scrollLeft}>
          <img aria-hidden="true" src="/schedule-and-save/assets/arrow_left.png" alt="Left Arrow" />
        </button>
        <button id="nextBtn" className={frequencyRightArrowNextBtn} onClick={scrollRight}>
          <img aria-hidden="true" src="/schedule-and-save/assets/arrow_right.png" alt="Right Arrow" />
        </button>
      </div>
      <div className={frequencyWidgetInfoContainer}>
        <div className={frequencyWidgetInfo}>
          {get(appLabels, 'PendingOrderContent.frequencyWidgetInfoText', '')}
          <span>
            {formatString(get(appLabels, 'PendingOrderContent.frequencyWidgetInfoText2', ''), {
              selectedWeek: selectedFrequencyWeek || '1 week',
            })}
          </span>
          {get(appLabels, 'PendingOrderContent.frequencyWidgetInfoText3', '')}
        </div>
      </div>
    </div>
  );
};
type OrderItem = {
  nextOrderItemId: number;
  subscriptionId: string;
  productBpn: string;
  productUpc: string;
  itemDescription: string;
  quantity: number;
  itemPrice: number;
  discountedItemPrice: number;
  totalPrice: number;
  isSubstitutable: boolean;
  substitutionValue: string;
  frequency: {
    count: number;
    displayFrequency: string;
  };
  everyWeeks: number;
  cancelledDate: string;
  itemPercentOff: number;
  savings?: any;
  clipped?: any;
  promoType?: string;
  promoText?: string;
  promoDescription?: string;
  linkPluNumber?: string;
};

const CouponModal = ({ isOpen, onClose, singleOffer }) => {
  const SNS_PROGRAM_TYPE = '1P_SNS';
  const B4U_APPNAME = 'b4u';

  useEffect(() => {
    if (window['appConfigServiceRef']) {
      window['appConfigServiceRef'].service.postUserInfoSource.next('initFunc');
      window['appConfigServiceRef'].service._postUserInfoSource.next('initFunc');
    }

    if (isOpen) {
      if (window['appConfigServiceRef']) {
        window['appConfigServiceRef'].service.postUserInfoSource.next('initFunc');
        window['appConfigServiceRef'].service._postUserInfoSource.next('initFunc');
      }
    }

    return () => {
      // Cleanup if needed when the modal is closed
    };
  }, [isOpen, singleOffer]);

  return (
    <Modal
      titleTextCenter={true}
      isOneTimeAddsModal={true}
      className="flex-row"
      titleText={'Offer Details'}
      onClose={onClose}
      show={isOpen}
      showBottomTransitionModal={isOpen} // Assuming showBottomTransitionModal should be the same as `isOpen`
      modalId={otaModalConfigId}
    >
      <div
        className={contentWrapperStyle}
        dangerouslySetInnerHTML={{
          __html: `
           <div class="main-wrapper www-wrapper nextgen-main-wrapper" data-enablev2theme="true">
            <div class="just-for-you-offer-detail section offer-details-nextgen-container">
              <span class="offer-details-wrapper">
               <offer-detail-al-v1
                  data-offer='${replace(JSON.stringify(singleOffer), /'/g, '')}'
                  data-configObj='${JSON.stringify({ programType: SNS_PROGRAM_TYPE, appName: B4U_APPNAME })}'>
                </offer-detail-al-v1>
              </span>
            </div>
          </div>
        `,
        }}
      ></div>
    </Modal>
  );
};

const renderCouponsAndSavings = (items, isClippedCoupons = false) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const openCouponAndOfferModal = (coupon) => {
    setSelectedOffer(coupon);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedOffer(null);
    setModalOpen(false);
  };
  return (
    <div>
      {isClippedCoupons
        ? map(items, (coupon, index) => (
            <>
              {get(coupon, 'displayOfferTxt') && (
                <div className={offerFontStyle} key={index}>
                  {coupon.omsOfferId && (coupon.hasOwnProperty('isClippable') ? coupon.isClippable : true) ? (
                    <>
                      <GreenTickIcon />
                      <button
                        className={`${couponsAndSavingsButtonStyle} link-sm `}
                        onClick={() => openCouponAndOfferModal(coupon)}
                      >
                        {get(coupon, 'displayOfferTxt')}
                      </button>
                    </>
                  ) : (
                    <>
                      <GreenTickIcon />
                      <span className={`${couponsAndSavingsButtonStyle} ${noUnderline}`}>
                        {get(coupon, 'displayOfferTxt')}
                      </span>
                    </>
                  )}
                </div>
              )}
            </>
          ))
        : map(items, (coupon, index) => (
            <>
              {get(coupon, 'displayOfferTxt') && get(coupon, 'display') && (
                <div className={offerFontStyle} key={index}>
                  <GreenTickIcon />
                  <button
                    className={`${couponsAndSavingsButtonStyle} link-sm`}
                    onClick={() => openCouponAndOfferModal(coupon)}
                  >
                    {get(coupon, 'displayOfferTxt')}
                  </button>
                </div>
              )}
            </>
          ))}
      <CouponModal isOpen={isModalOpen} onClose={closeModal} singleOffer={selectedOffer} />
    </div>
  );
};

const flexibleDiscounts = (isFutureOrder = false, flexibleDiscountDescription = '', ecomAppliedText = '') => {
  const noFlexibleDiscount =
    flexibleDiscountDescription === appLabels.FutureOrderContent.subscriptionWithoutDiscountText;
  return cond([
    [
      () => isFutureOrder,
      () =>
        flexibleDiscountDescription && (
          <div className={`${offerFontStyle} ${noFlexibleDiscount ? noFlexibleDiscountInfoIconCenter : ''}`}>
            {!noFlexibleDiscount && <GreenTickIcon />}
            <span className={noFlexibleDiscount ? subscriptionWithoutSavingStyle : couponsAndSavingsSpanStyle} data-testid = "flexible-discount-text">
              {flexibleDiscountDescription}
            </span>
            {noFlexibleDiscount && (
              <OrderInfoIcon
                className={infoIconStyle}
                width={16}
                popoverContent={appLabels.FutureOrderContent.subscriptionWithoutDiscountInfoText}
                analyticsPageView={''}
                label={appLabels.FutureOrderContent.estimatedTooltipLabel}
              />
            )}
          </div>
        ),
    ],
    [
      () => true,
      () =>
        ecomAppliedText && (
          <div className={offerFontStyle}>
            <GreenTickIcon />
            <span className={couponsAndSavingsSpanStyle}>{ecomAppliedText}</span>
          </div>
        ),
    ],
  ])();
};

const RefreshIconBlack = () => (
  <svg className="rightArrow" width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.0085 9.73393C19.7511 0.991427 7.84867 -0.734327 4.34522 6.85185C4.17147 7.22808 3.72586 7.39247 3.34992 7.21903C2.97398 7.0456 2.81008 6.6 2.98383 6.22378C6.94329 -2.34984 20.27 -0.965712 21.4418 8.80249L22.2765 7.33324C22.4811 6.97309 22.939 6.847 23.2991 7.05161C23.6593 7.25623 23.7854 7.71406 23.5807 8.0742L21.2754 12.1319C21.1514 12.3503 20.9262 12.4921 20.6756 12.5096C20.4251 12.5271 20.1824 12.4181 20.0291 12.2191L17.1815 8.52168C16.9288 8.19352 16.9899 7.72259 17.3181 7.46985C17.6463 7.21711 18.1172 7.27825 18.3699 7.60642L20.0085 9.73393Z"
      fill="#1F1E1E"
    />
    <path
      d="M3.97045 9.78072C3.81719 9.58173 3.57449 9.47266 3.32393 9.49018C3.07337 9.5077 2.84822 9.64948 2.72414 9.86787L0.418825 13.9256C0.214213 14.2857 0.340299 14.7436 0.700448 14.9482C1.0606 15.1528 1.51842 15.0267 1.72304 14.6665L2.55778 13.1973C3.72957 22.9655 17.0563 24.3496 21.0158 15.776C21.1895 15.3998 21.0256 14.9542 20.6497 14.7807C20.2737 14.6073 19.8281 14.7717 19.6544 15.1479C16.1511 22.7337 4.24845 21.0088 3.99113 12.2663L5.62965 14.3934C5.8824 14.7215 6.35332 14.7827 6.68149 14.5299C7.00965 14.2772 7.07079 13.8063 6.81805 13.4781L3.97045 9.78072Z"
      fill="#1F1E1E"
    />
  </svg>
);
const RefreshIconRed = () => (
  <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.0085 9.73369C19.7511 0.991183 7.84867 -0.734571 4.34522 6.85161C4.17147 7.22783 3.72586 7.39223 3.34992 7.21879C2.97398 7.04535 2.81008 6.59976 2.98383 6.22353C6.94329 -2.35008 20.27 -0.965956 21.4418 8.80225L22.2765 7.33299C22.4811 6.97285 22.939 6.84676 23.2991 7.05137C23.6593 7.25598 23.7854 7.71381 23.5807 8.07396L21.2754 12.1317C21.1514 12.3501 20.9262 12.4918 20.6756 12.5094C20.4251 12.5269 20.1824 12.4178 20.0291 12.2188L17.1815 8.52144C16.9288 8.19327 16.9899 7.72235 17.3181 7.46961C17.6463 7.21686 18.1172 7.278 18.3699 7.60617L20.0085 9.73369Z"
      fill="#8C0E12"
    />
    <path
      d="M3.97045 9.78048C3.81719 9.58148 3.57449 9.47242 3.32393 9.48994C3.07337 9.50746 2.84822 9.64924 2.72414 9.86763L0.418825 13.9253C0.214213 14.2855 0.340299 14.7433 0.700448 14.9479C1.0606 15.1525 1.51842 15.0264 1.72304 14.6663L2.55778 13.197C3.72957 22.9652 17.0563 24.3494 21.0158 15.7758C21.1895 15.3995 21.0256 14.9539 20.6497 14.7805C20.2737 14.6071 19.8281 14.7715 19.6544 15.1477C16.1511 22.7335 4.24845 21.0086 3.99113 12.2661L5.62965 14.3931C5.8824 14.7213 6.35332 14.7824 6.68149 14.5297C7.00965 14.2769 7.07079 13.806 6.81805 13.4779L3.97045 9.78048Z"
      fill="#8C0E12"
    />
  </svg>
);

const OtaToSnsConversion = (props) => {
  //TODO: Item as props and need to fetch the frequencies also.
  const item: OrderItem = get(props, 'item');
  const setShowOtaToSnsModal = get(props, 'setShowOtaToSnsModal');
  const [quantity, setQuantity] = useState(item.quantity);
  const frequencyOptions = get(props, 'otaToSnsFrequencyOptions', []);
  const setLoading = get(props, 'setLoading');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [frequency, setSelectedFrequency] = useState(get(frequencyOptions, [currentIndex, 'displayFrequency'], ''));
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;
  const discount = get(props, 'discount', '');

  const user = get(currentSessionContext, 'user');
  const { appData, showToast, showTooltip } = useApp();
  const [isMobileScreenSize, setIsMobileScreenSize] = useState(isMobileScreen());

  useEffect(() => {
    const handleResize = () => {
      setIsMobileScreenSize(isMobileScreen());
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const saveOtaToSnsItem = async () => {
    setShowOtaToSnsModal(false);
    setLoading(true);

    const payload = {
      itemBpn: get(item, 'productBpn'),
      qty: parseInt(String(quantity)),
      frequency: {
        unit: get(frequency, 'unit', ''),
        count: get(frequency, 'count', 0),
      },
    };
    try {
      const response = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        saveOtaToSnsItemApi(appData.environmentVariables, user, payload),
      );
      if (
        (get(response, 'data') && get(response.data, 'type') !== 'SUCCESS') ||
        get(response.data, 'statusCode') !== 200
      ) {
        throw new Error(get(response, 'message', ''));
      }
      showToast(
        'info',
        formatString(appLabels.PendingOrderContent.otaToSnsSubscribeSuccess, {
          frequency: get(frequency, 'count', 0),
          week: get(frequency, 'count', 0) === 1 ? 'week' : 'weeks',
        }),
      );
      await invoke(currentItemsContext, 'fetchUpcomingOrdersInfo');
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showToast('error', appLabels.PendingOrderContent.otaToSnsSubscribeError);
    }
  };

  return (
    <>
      <div className={subToOtaModal}>
        <div className={itemContentContainer}>
          <Asset productBpc={get(item, 'productBpn')} className={itemContentAssetStyle} />
        </div>
        <div className={frequencyContainer}>
          <div>
            <div className={itemContent}>
              {/*
              <div className={approxLabelTextStyle}>{appLabels.PendingOrderContent.approxLabelText}</div>
*/}
              <div className={itemPriceContainerStyle}>
                <div
                  className={
                    get(item, 'discountedItemPrice') ? `${itemPriceDiscountedStyle} ${itemHighlightStyle}` : ''
                  }
                >
                  {get(item, 'discountedItemPrice') ? `$${get(item, 'discountedItemPrice')}` : ''}
                </div>
                <div
                  className={
                    get(item, 'discountedItemPrice')
                      ? `${itemPriceRegularStyle} ${itemPriceStrikeThroughStyle}`
                      : `${itemPriceDiscountedStyle}`
                  }
                >
                  ${get(item, 'itemPrice')}
                </div>
              </div>
              <p className={approxLabelTextStyle} data-testid="discount-description">
                {discount
                  ? formatString(appLabels.PendingOrderContent.withScheduleAndSaveText, {
                      discount: discount,
                    })
                  : appLabels.PendingOrderContent.ScheduleAndSaveText}
              </p>
              <div className={itemDescriptionTextStyle}>{get(item, 'itemDescription')}</div>
            </div>
            <QuantityInput
              item={item}
              subscriptionId={get(item, 'subscriptionId')}
              quantity={quantity}
              maxQuantity={20}
              className={otaToSnsModalQunatityStyle}
              isOtaToSnsModal={true}
              onChange={(selectedQuantity) => {
                setQuantity(selectedQuantity);
              }}
            ></QuantityInput>
          </div>

          {isMobileScreenSize ? (
            ''
          ) : (
            <FrequencyWidget
              frequencyOptions={frequencyOptions}
              onChange={(frequency) => {
                setSelectedFrequency(frequency);
              }}
            />
          )}
        </div>
      </div>
      {isMobile() || isMobileScreenSize ? (
        <FrequencyWidget
          frequencyOptions={frequencyOptions}
          onChange={(frequency) => {
            setSelectedFrequency(frequency);
          }}
        />
      ) : null}
      <div className={subToOtaButtonContainer}>
        <button
          className={`${subToOtaNotNowButton} ${subToOtaButton}`}
          onClick={() => {
            setShowOtaToSnsModal(false);
          }}
        >
          Not now
        </button>
        <button
          className={subToOtaButton}
          onClick={() => {
            saveOtaToSnsItem();
          }}
        >
          Subscribe
        </button>
      </div>
    </>
  );
};
// method to format cancelledDate to : Cancelled on Mar 22
const formatCancelledDate = (cancelledDate) => {
  const date = new Date(cancelledDate);
  const month = date.toLocaleString('en-us', { month: 'short' });
  const dayNumber = date.getDate();
  return `Cancelled on ${month} ${dayNumber}`;
};

const ScheduledItem = (props) => {
  const item: OrderItem = get(props, 'item');
  const snsSavings = get(item, 'snsSavings', {});
  const ecomDisplayText = get(snsSavings, 'ecomDisplayText', '');
  const discountValue = ecomDisplayText.match(/Schedule & Save (\d+)%/);
  const discount = discountValue ? `${parseInt(get(discountValue, '[1]', ''))}%` : '';
  const ecomAppliedText = get(snsSavings, 'ecomAppliedText', '');
  const showRemoveItemModal = get(props, 'showRemoveItemModal');
  const isCancelledItem: boolean = get(props, 'isCancelledItem');
  const showSkipOnceLink: boolean = get(props, 'showSkipOnceLink');
  const showSubstitutionButton: boolean = get(props, 'showSubstitutionButton');
  const showDeleteIcon: boolean = get(props, 'showDeleteIcon', false);
  const cancellationReasons: string = get(props, 'cancellationReasons');
  const displayItemDate = get(props, 'displayItemDate', true);
  const displayItemFrequency = get(props, 'displayItemFrequency', true);
  const editableQuantity = get(props, 'editableQuantity', true);
  const refreshHandler: Function = get(props, 'refreshHandler');
  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;
  const frequencyOptions = get(currentItemsContext, 'frequencyOptions');
  const otaToSnsFrequencyOptions = get(currentItemsContext, 'otaToSnsFrequencyOptions');
  const displaySubstitutionCheckbox = get(props, 'displaySubstitutionCheckbox', false);
  const displayCancelButton = get(props, 'displayCancelButton', true);
  const showEditSubstitution: string = get(props, 'showEditSubstitution', 'NONE');
  const quantityResetOnError: boolean = get(props, 'quantityResetOnError', false);
  const isOneTimeAddList: boolean = get(props, 'isOneTimeAddList', false);
  const otaToSnsFlag = get(props, 'otaToSnsFlag', false);
  const orderType: string = get(props, 'orderType', 'upcoming');

  //TODO new flag should receive here
  const flexibleDiscountDescription = get(item, 'flexibleDiscountDesc', '');

  // execute only once on component load
  const [weeks, setWeeks] = useState(get(item, 'frequency.displayFrequency'));
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const { appData, showToast, showTooltip } = useApp();
  const [quantity, setQuantity] = useState(item.quantity);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [showOtaToSnsModal, setShowOtaToSnsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkedState, setCheckedState] = useState(item.isSubstitutable);

  const isClippedCoupons = true;

  useEffect(() => {
    setQuantity(item.quantity);
  }, [item]);

  const openPopoverModal = (evt) => {
    showTooltip({
      message: appLabels.ScheduledItems.popOverText,
      link: SNS_LINK,
      target: get(evt, 'target'),
    });

    evt.stopPropagation();
    analyticsEvents('tool-tip', 'link');
  };

  const submitFrequencyHandler = async (selectedOption) => {
    setShowFrequencyModal(false);
    setLoading(true);
    // create payload for update
    const updateFrequencyPayload = {
      subscriptionId: get(item, 'subscriptionId'),
      frequency: find(frequencyOptions, { displayFrequency: selectedOption }), //get(frequencyOptions, selectedOption),
      itemBpn: get(item, 'productBpn'),
      isReplinishItem: get(item, 'isReplinishItem', true),
    };
    try {
      const updateItemResponse = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        updateItemInfo(appData.environmentVariables, user, updateFrequencyPayload),
      );
      // check update response for errors
      if (
        (get(updateItemResponse, 'data') && get(updateItemResponse.data, 'type') !== 'SUCCESS') ||
        get(updateItemResponse.data, 'statusCode') !== 200
      ) {
        throw new Error(get(updateItemResponse, 'message', ''));
      }
      showToast(
        'info',
        `${get(item, 'itemDescription')} frequency will be changed beginning after your next order is completed.`,
      );
      // update frequency state
      setWeeks(get(updateFrequencyPayload, 'frequency.displayFrequency', ''));
      setLoading(false);
      if (get(updateFrequencyPayload, 'frequency.count') !== get(props, 'item.frequency.count')) {
        analyticsEvents(
          'ar:select-item-frequency|change',
          'click',
          '',
          get(updateFrequencyPayload, 'frequency.displayFrequency'),
        );
      }
    } catch (error) {
      setLoading(false);
      showToast('error', appLabels.ApiError.message);
    }
  };

  const submitCancellation = async (data) => {
    setShowCancelModal(false);
    setLoading(true);

    // deactivateReason must be data.cancelOption unless is other
    const deactivateReason = data.cancelOption.toLowerCase() === 'other' ? data.otherDescription : data.cancelOption;
    // find inside cancellationReasons the reasonSequence for the selected reason default to 99
    const reasonSequence =
      props.cancellationReasons.find((reason) => reason.reason === deactivateReason)?.reasonSequence || 99;

    // create payload for cancellation
    const cancellationPayload = {
      subscriptionId: item.subscriptionId,
      deactivate: true,
      deactivateReason: deactivateReason,
      reasonSequence: reasonSequence,
      itemBpn: get(item, 'productBpn'),
      isReplinishItem: get(item, 'isReplinishItem', true),
    };

    try {
      const cancelItemResponse = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        cancelSubscription(appData.environmentVariables, user, cancellationPayload),
      );
      // check update response for errors
      if (
        (get(cancelItemResponse, 'data') && get(cancelItemResponse.data, 'type') !== 'SUCCESS') ||
        get(cancelItemResponse.data, 'statusCode') !== 200
      ) {
        throw new Error(get(cancelItemResponse, 'message', ''));
      }
      showToast(
        'info',
        formatString(appLabels.CancelledItems.cancelledText, {
          itemDescription: item.itemDescription,
        }),
      );
      refreshHandler();
      await invoke(currentItemsContext, 'fetchUpcomingOrdersInfo');
      setLoading(false);
      analyticsEvents('ar:cancel-item-subscription|confirm', 'click', data.cancelOption);
    } catch (error) {
      setLoading(false);
      showToast('error', appLabels.ApiError.message);
    }
  };

  const updateSubstitutable = async (checked) => {
    setLoading(true);
    const updateSubstitutablePayload = {
      subscriptionId: item.subscriptionId,
      isSubstitutable: checked,
    };

    try {
      const updateItemResponse = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        updateItemInfo(appData.environmentVariables, user, updateSubstitutablePayload),
      );
      // check update response for errors
      if (
        (get(updateItemResponse, 'data') && get(updateItemResponse.data, 'type') !== 'SUCCESS') ||
        get(updateItemResponse.data, 'statusCode') !== 200
      ) {
        throw new Error(get(updateItemResponse, 'message', ''));
      }
      setLoading(false);
      setCheckedState(checked);
    } catch (error) {
      showToast('error', appLabels.SubstitutableSelector.updateFailedText);
      setLoading(false);
    }
  };

  const handleCheckboxChange = async () => {
    if (loading) {
      return;
    }
    await updateSubstitutable(!checkedState);
    analyticsEvents(`${checkedState ? 'dont-' : ''}allow-sub`, 'link');
  };

  const analyticsEvents = (name, eventType, message?, updatedFrequency?) => {
    try {
      if (window['AB']?.DATALAYER) {
        if (eventType === 'link') {
          window['AB'].DATALAYER.scheduledAndSaveItemsLinkClick(name, props);
        } else {
          const analyticsObj = {
            productID: props.item.productBpn,
            updatedFrequency: updatedFrequency,
          };
          window['AB'].DATALAYER.scheduledAndSaveItemsModalEvents(eventType, name, analyticsObj, message);
        }
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  return (
    <div key={item.nextOrderItemId} className={`${mainContainerStyle}`}>
      {loading && <Loader useOverlay />}
      {displayItemDate ? (
        <div className={`word-highlight-xs ${dateStyle}`}>
          {!isCancelledItem ? (
            <span>
              {get(item, 'nextFulfillmentDate') && get(item, 'nextFulfillmentDate') !== 'null'
                ? formatNextFulfillmentDate(
                    get(item, 'nextFulfillmentDate'),
                    get(item, 'fulfillmentStartTime'),
                    get(item, 'fulfillmentEndTime'),
                  )
                : null}
            </span>
          ) : (
            <span>{formatCancelledDate(item.cancelledDate)}</span>
          )}
        </div>
      ) : null}
      {get(item, 'itemStatus') === 'Inactive' && orderType === 'upcoming' && (
        <div
          style={{
            color: '#8c0e12',
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
        >
          <svg 
            className={warningIconStyle}
            viewBox="0 0 16 16" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.457 2.573c.661-1.097 2.425-1.097 3.086 0l5.333 8.853c.648 1.077-.184 2.407-1.543 2.407H2.667c-1.359 0-2.191-1.33-1.543-2.407L6.457 2.573zM8 5.333a.667.667 0 0 1 .667.667v2.667a.667.667 0 0 1-1.334 0V6A.667.667 0 0 1 8 5.333zM8 12a.667.667 0 1 0 0-1.333A.667.667 0 0 0 8 12z"
              fill="#8c0e12"
            />
          </svg>
          <span>This item is inactive and you may or may not receive it in your next order.</span>
        </div>
      )}
      <div className={itemContainerStyle}>
        <div className={ItemImageContainer}>
          <Asset productBpc={item.productBpn} className={assetStyle} />
        </div>
        <div className={itemInfoStyle}>
          <div className={descriptionContainerStyle}>
            <div className={`body-text ${itemNameStyle}`}>{item.itemDescription}</div>
            {isCancelledItem ? (
              <div className={cancelledQuantityStyle}>{quantity}</div>
            ) : editableQuantity ? (
              <QuantityInput
                showDeleteIcon={showDeleteIcon}
                showRemoveItemModal={showRemoveItemModal}
                item={item}
                subscriptionId={item.subscriptionId}
                quantity={quantity}
                maxQuantity={20}
                onChange={(q) => setQuantity(q)}
                quantityResetOnError={quantityResetOnError}
              ></QuantityInput>
            ) : (
              <span>Qty: {quantity}</span>
            )}
            <ItemPrice
              unitPrice={item.itemPrice}
              quantity={quantity}
              discountPrice={item.discountedItemPrice}
            ></ItemPrice>
          </div>

          {!isCancelledItem && (
            <Modal
              titleText="Select item frequency"
              description=""
              onClose={() => setShowFrequencyModal(false)}
              show={showFrequencyModal}
              maxWidth={28}
            >
              <SelectItemFrequency
                frequencyOptions={frequencyOptions}
                submitHandler={submitFrequencyHandler}
                itemFrequency={weeks}
              />
            </Modal>
          )}
          {!isCancelledItem && displaySubstitutionCheckbox ? (
            <div className={checkboxRowStyle} onClick={handleCheckboxChange} data-testid="allow-substitution-checkbox">
              <Checkbox checked={checkedState}>
                <span className="body-text-xs">Allow substitution</span>
              </Checkbox>
              <button
                className={allowSubstitutionTooltipStyle}
                onClick={openPopoverModal}
                aria-label="Allow substitution tooltip"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-info-circle"
                  viewBox="0 0 16 16"
                  aria-hidden={true}
                >
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                </svg>
              </button>
            </div>
          ) : null}
          {flexibleDiscounts(true, flexibleDiscountDescription, '')}
          {flexibleDiscounts(false, '', ecomAppliedText)}
          {renderCouponsAndSavings(get(item, 'clipped', []), isClippedCoupons)}
          {renderCouponsAndSavings(get(item, 'savings', []), false)}
          {otaToSnsFlag && ecomDisplayText && (
            <div className={otaToSnsContainerStyle}>
              <div
                onClick={() => {
                  setShowOtaToSnsModal(true);
                }}
                data-testid="ota-to-sns-checkbox"
              >
                <Checkbox checked={false}>
                  <span className={otaToSnsCheckBoxTextStyle}>{ecomDisplayText}</span>
                </Checkbox>
              </div>
            </div>
          )}
          <div className={buttonsContainerStyle}>
            {showSkipOnceLink ? (
              <div>
                <SkipItemOnceLink item={item} />
              </div>
            ) : null}

            {displayItemFrequency ? (
              <div className={actionsRowStyle}>
                <button
                  aria-label="select frequency"
                  className={`${frequencyButtonStyle} link-sm`}
                  onClick={() => {
                    setShowFrequencyModal(true);
                    analyticsEvents('ar:select-item-frequency|view', 'view');
                  }}
                >
                  {!isCancelledItem && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M13.3399 7.15619C13.1684 1.32787 5.23333 0.177808 2.89778 5.23506C2.78195 5.48588 2.48488 5.59547 2.23425 5.47985C1.98363 5.36422 1.87436 5.06716 1.99019 4.81634C4.62979 -0.89931 13.5141 0.0232816 14.2955 6.53529L14.852 5.55574C14.9884 5.31564 15.2936 5.23158 15.5337 5.36799C15.7738 5.5044 15.8579 5.80962 15.7215 6.04972L14.1846 8.75486C14.1019 8.90045 13.9518 8.99497 13.7847 9.00665C13.6177 9.01833 13.4559 8.94562 13.3537 8.81296L11.4553 6.34804C11.2868 6.12926 11.3276 5.81531 11.5464 5.64682C11.7651 5.47832 12.0791 5.51908 12.2476 5.73786L13.3399 7.15619Z"
                        fill="#8C0E12"
                      />
                      <path
                        d="M2.64794 7.18748C2.54577 7.05481 2.38397 6.9821 2.21693 6.99378C2.04989 7.00547 1.89979 7.09999 1.81707 7.24558L0.280193 9.95072C0.143785 10.1908 0.227843 10.496 0.467942 10.6324C0.708041 10.7688 1.01326 10.6848 1.14967 10.4447L1.70616 9.46519C2.48736 15.9773 11.3718 16.9001 14.0115 11.1843C14.1273 10.9335 14.018 10.6365 13.7674 10.5208C13.5168 10.4052 13.2197 10.5148 13.1039 10.7656C10.7684 15.8228 2.83327 14.6729 2.66173 8.84455L3.75408 10.2626C3.92257 10.4814 4.23652 10.5221 4.4553 10.3536C4.67408 10.1851 4.71484 9.87117 4.54634 9.6524L2.64794 7.18748Z"
                        fill="#8C0E12"
                      />
                    </svg>
                  )}

                  <span className={repeatTextStyle}>Repeats:</span>
                  <span className="frequencyLabelText">
                    {formatString(appLabels.ScheduledItems.frequencyLabel, {
                      weeks: weeks,
                    })}
                  </span>
                </button>
              </div>
            ) : null}

            {displayCancelButton && (
              <div className={actionsRowStyle}>
                {displayCancelButton ? (
                  isCancelledItem ? (
                    <div className={`${cancelSubscriptionButtonStyle} link-sm`}>Schedule Item</div>
                  ) : (
                    <>
                      <div>
                        <button
                          onClick={() => {
                            setShowCancelModal(true);
                            analyticsEvents('ar:cancel-item-subscription|view', 'view');
                          }}
                          type="button"
                          aria-label="cancel subscription"
                          className={`${cancelSubscriptionButtonStyle} link-sm`}
                        >
                          Cancel subscription
                        </button>
                      </div>
                      <Modal
                        titleText="Cancel item subscription"
                        description={formatString(appLabels.ScheduledItems.cancellationDescription, {
                          itemName: item.itemDescription,
                        })}
                        onClose={() => setShowCancelModal(false)}
                        show={showCancelModal}
                        maxWidth={28}
                        className={css`
                          margin-top: 11.5rem;

                          @media (max-width: 641px) {
                            margin-top: initial;
                          }

                          & p[role='heading'] {
                            font-size: 1.25rem;
                            font-weight: 600;
                          }
                        `}
                      >
                        <CancelItemSubscription
                          cancellationReasons={cancellationReasons}
                          submitHandler={submitCancellation}
                        />
                      </Modal>
                    </>
                  )
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {showSubstitutionButton ? (
        <div className={`${checkboxRowStyle} ${editSubstitutionLink}`}>
          <EditSubstitutionLink
            item={item}
            showSubstitutionButton={showSubstitutionButton}
            className={upcomingOrderSubstitutionLinkStyle}
          />
        </div>
      ) : null}
      <Modal
        titleText={appLabels.PendingOrderContent.otaToSnsModalHeadline}
        description={
          discount
            ? formatString(appLabels.PendingOrderContent.otaToSnsModalWithDiscountSubHeadline, {
                discount: discount,
              })
            : appLabels.PendingOrderContent.otaToSnsModalNoDiscountSubHeadline
        }
        onClose={() => setShowOtaToSnsModal(false)}
        show={showOtaToSnsModal}
        className={flexRow}
        titleTextCenterForOtaToSnsModal={true}
        showStrokeLines={false}
        showBottomTransitionModal={true}
        isOtaToSnsModal={true}
        overFlowBodyHidden={true}
      >
        <OtaToSnsConversion
          item={item}
          quantity={quantity}
          setLoading={setLoading}
          setQuantity={setQuantity}
          quantityResetOnError={quantityResetOnError}
          otaToSnsFrequencyOptions={otaToSnsFrequencyOptions}
          setShowOtaToSnsModal={setShowOtaToSnsModal}
          discount={discount}
        />
      </Modal>
    </div>
  );
};

ScheduledItem.propTypes = {
  children: PropTypes.any,
  item: PropTypes.any,
  showRemoveItemModal: PropTypes.bool,
  showSkipOnceLink: PropTypes.bool,
  showSubstitutionButton: PropTypes.bool,
  showDeleteIcon: PropTypes.bool,
  isCancelledItem: PropTypes.bool,
  isOneTimeAddList: PropTypes.bool,
  cancellationReasons: PropTypes.array,
  refreshHandler: PropTypes.func,
  displayItemDate: PropTypes.bool,
  displayItemFrequency: PropTypes.bool,
  editableQuantity: PropTypes.bool,
  displaySubstitutionCheckbox: PropTypes.bool,
  displayCancelButton: PropTypes.bool,
  showEditSubstitution: PropTypes.oneOf(['NONE', 'UPCOMING_ORDER', 'ALL_ITEMS']),
  substitutionValue: PropTypes.oneOf(['Best Available', 'Substitute With', 'Do Not Substitute']),
  quantityResetOnError: PropTypes.bool,
  otaToSnsFlag: PropTypes.bool,
  otaToSnsFrequencyOptions: PropTypes.array,
  orderType: PropTypes.oneOf(['upcoming', 'future', 'all']),
};
ScheduledItem.defaultProps = {
  isCancelledItem: false,
  cancellationReasons: [],
  editableQuantity: true,
  refreshHandler: () => {},
};

export default ScheduledItem;
